import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/routeConfig';
import { AccountDetailsTab } from '@/shared/config/account-details-tabs';
import { getAccountDetailsPath } from '@/shared/utils/account-details-routes';
import { User, Organization } from '@/shared/types/userTypes';
import {
  deriveOrgRoleFromUser,
  isCustomerUserProfile,
  isPlatformUserProfile,
  isTenantUserProfile,
} from '@/shared/utils/auth-utils';
import { OrgRole } from '@/shared/types/userRoleTypes';
import { apiClient } from '@/shared/services/apiConfig';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@/shared/utils/logger';
import { fetchCurrentUser, logout } from '@/app/auth/services/auth';
import { isGuestAuthPath } from '@/app/shared/CatchAllRoute';
import { toast } from 'sonner';
import { ERROR } from '@/config/toast-message-config';
import { CustomerDetailsTab } from '@/shared/config/customer-details-tabs';
import { getCustomerDetailsPath } from '@/shared/utils/customer-details-routes';
import { Customer } from '@/shared/types/customerTypes';

const STORAGE_KEYS = {
  CURRENT_USER: 'currentUser',
  SELECTED_REGION: 'selectedRegion',
  SELECTED_ORGANIZATION: 'selectedOrganization',
  SELECTED_CUSTOMER_PARENT_ORGANIZATION: 'selectedCustomerParentOrganization',
} as const;

function parseStorage<T>(raw: string | null): T | null {
  if (raw == null || raw === 'undefined' || raw === 'null') return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function getDefaultOrganization(organizations: Organization[] | null | undefined): Organization | null {
  return organizations?.find((organization) => organization.isDefault) ?? organizations?.[0] ?? null;
}

/** Default org + parent org for a customer user (prefers org marked isDefault). */
function getDefaultCustomerSelection(user: User): { organization: Organization; parentOrganization: Organization | null } | null {
  const organizations = user.organizations ?? [];
  if (!organizations.length) return null;

  for (const organization of organizations) {
    const defaultParentOrganization = organization.parentOrganizations?.find((entry) => entry.isDefault);
    if (defaultParentOrganization) {
      return { organization, parentOrganization: defaultParentOrganization };
    }
  }

  for (const organization of organizations) {
    const parentOrganization = getDefaultOrganization(organization.parentOrganizations);
    if (parentOrganization) {
      return { organization, parentOrganization };
    }
  }

  const organization = getDefaultOrganization(organizations);
  if (organization) {
    return { organization, parentOrganization: null };
  }

  return null;
}

/** Resolve an org under the customer's accessible organizations list. */
function findCustomerOrganization(
  organizations: Organization[] | null | undefined,
  storedOrganizationId: string,
  parentOrganizationId?: string | null,
): { organization: Organization; parentOrganization: Organization | null } | null {
  const list = organizations ?? [];
  const organization = list.find((entry) => entry.organizationId === storedOrganizationId);
  if (!organization) {
    // Legacy mismatch: stored id might have been a parent id — try reverse lookup.
    for (const entry of list) {
      const parentOrganization = entry.parentOrganizations?.find(
        (parent) => parent.organizationId === storedOrganizationId,
      );
      if (parentOrganization) {
        return { organization: entry, parentOrganization };
      }
    }
    return null;
  }

  if (parentOrganizationId) {
    const parentOrganization = organization.parentOrganizations?.find(
      (entry) => entry.organizationId === parentOrganizationId,
    );
    if (parentOrganization) {
      return { organization, parentOrganization };
    }
  }

  return {
    organization,
    parentOrganization: getDefaultOrganization(organization.parentOrganizations),
  };
}

function getDefaultRegion(user: User): string | null {
  return user.organizations?.find((organization) => organization.isDefault)?.regionCode
    ?? user.availableRegions?.[0]?.regionCode
    ?? null;
}

function applyUserSelection(
  user: User,
  persistOrganizationSelection: (
    organization: Organization | null,
    parentOrganization?: Organization | null,
  ) => void,
  setSelectedRegionCode: (region: string | null) => void,
) {
  const storedOrganization = parseStorage<Organization>(
    sessionStorage.getItem(STORAGE_KEYS.SELECTED_ORGANIZATION),
  );
  const storedParentOrganization = parseStorage<Organization>(
    sessionStorage.getItem(STORAGE_KEYS.SELECTED_CUSTOMER_PARENT_ORGANIZATION),
  );
  const region = sessionStorage.getItem(STORAGE_KEYS.SELECTED_REGION);

  if (region && region !== 'undefined' && region !== 'null') {
    setSelectedRegionCode(region);
  }

  if (isPlatformUserProfile(user)) {
    sessionStorage.removeItem(STORAGE_KEYS.SELECTED_CUSTOMER_PARENT_ORGANIZATION);
    if (storedOrganization?.organizationId) {
      persistOrganizationSelection(storedOrganization, null);
    }
    return;
  }

  if (isTenantUserProfile(user)) {
    let organizationToUse: Organization | null = null;
    if (storedOrganization?.organizationId) {
      const belongsToUser = user.organizations?.some(
        (organization) => organization.organizationId === storedOrganization.organizationId,
      ) ?? false;
      if (belongsToUser) {
        organizationToUse = storedOrganization;
      }
    }
    if (!organizationToUse) {
      organizationToUse = getDefaultOrganization(user.organizations);
    }
    if (organizationToUse) {
      persistOrganizationSelection(organizationToUse, null);
    }
    return;
  }

  if (isCustomerUserProfile(user)) {
    let selection: { organization: Organization; parentOrganization: Organization | null } | null = null;

    if (storedOrganization?.organizationId) {
      selection = findCustomerOrganization(
        user.organizations,
        storedOrganization.organizationId,
        storedParentOrganization?.organizationId,
      );
    }
    if (!selection) {
      selection = getDefaultCustomerSelection(user);
    }
    if (selection) {
      persistOrganizationSelection(selection.organization, selection.parentOrganization);
    }
  }
}

interface UserContextType {
  currentUser: User | null;
  selectedOrganization: Organization | null;
  /** Active customer for customer users (null for platform/tenant). */
  selectedParentOrganization: Organization | null;
  selectedRegionCode: string | null;
  orgRole: OrgRole | null;
  isLoading: boolean;
  isInitialized: boolean;
  completeLogin: () => Promise<void>;
  handleLogout: () => Promise<void>;
  handleRegionChange: (regionCode: string | null) => void;
  setSelectedOrganizationOnly: (organization: Organization | null, parentOrganization?: Organization | null) => void;
  refreshAuth: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [selectedParentOrganization, setSelectedParentOrganization] = useState<Organization | null>(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasBootstrappedRef = useRef(false);

  const persistOrganizationSelection = useCallback((
    organization: Organization | null,
    parentOrganization: Organization | null = null,
  ) => {
    setSelectedOrganization(organization);
    setSelectedParentOrganization(parentOrganization);

    if (organization) {
      sessionStorage.setItem(STORAGE_KEYS.SELECTED_ORGANIZATION, JSON.stringify(organization));
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.SELECTED_ORGANIZATION);
    }

    if (parentOrganization) {
      sessionStorage.setItem(STORAGE_KEYS.SELECTED_CUSTOMER_PARENT_ORGANIZATION, JSON.stringify(parentOrganization));
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.SELECTED_CUSTOMER_PARENT_ORGANIZATION);
    }
  }, []);

  const navigateAfterLogin = useCallback((user: User) => {
    if (isPlatformUserProfile(user)) {
      persistOrganizationSelection(null, null);
      const region = getDefaultRegion(user);
      if (region) {
        sessionStorage.setItem(STORAGE_KEYS.SELECTED_REGION, region);
        setSelectedRegionCode(region);
      }
      navigate(ROUTES.ACCOUNTS, { state: { region: region ?? undefined }, replace: true });
      return;
    }

    if (isTenantUserProfile(user)) {
      const organization = getDefaultOrganization(user.organizations);
      if (organization?.organizationId) {
        const region = organization.regionCode ?? undefined;
        if (region) {
          sessionStorage.setItem(STORAGE_KEYS.SELECTED_REGION, region);
          setSelectedRegionCode(region);
        }
        persistOrganizationSelection(organization, null);
        navigate(
          getAccountDetailsPath(organization.organizationId, AccountDetailsTab.DASHBOARD),
          { replace: true },
        );
      } else {
        navigate(ROUTES.LOGIN, { replace: true });
      }
      return;
    }

    if (isCustomerUserProfile(user)) {
      const selection = getDefaultCustomerSelection(user);
      if (selection) {
        const region = selection.organization.regionCode ?? undefined;
        if (region) {
          sessionStorage.setItem(STORAGE_KEYS.SELECTED_REGION, region);
          setSelectedRegionCode(region);
        }
        persistOrganizationSelection(selection.organization, selection.parentOrganization);

        const customer: Customer = {
          customerId: selection.organization.organizationId,
          name: selection.organization.name,
          role: selection.organization.role,
          isSelfServiceAllowed: selection.organization.isSelfServiceAllowed,
        };

        navigate(getCustomerDetailsPath(customer.name, CustomerDetailsTab.DASHBOARD), {
          replace: true,
          state: { customer },
        });
      } else {
        navigate(ROUTES.LOGIN, { replace: true });
      }
      return;
    }

    navigate(ROUTES.LOGIN, { replace: true });
  }, [navigate, persistOrganizationSelection]);

  const refreshAuth = useCallback(async () => {
    const user = await fetchCurrentUser();
    apiClient.setAuthClaims(user);
    sessionStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    // Cookie session restore — once per mount. Do not depend on navigate or
    // other callbacks or /me will re-run on every route change.
    if (hasBootstrappedRef.current) return;
    hasBootstrappedRef.current = true;

    const bootstrap = async () => {
      try {
        const user = await fetchCurrentUser();
        apiClient.setAuthClaims(user);
        sessionStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
        setCurrentUser(user);
        applyUserSelection(user, persistOrganizationSelection, setSelectedRegionCode);
      } catch {
        const rawUser = sessionStorage.getItem(STORAGE_KEYS.CURRENT_USER);
        if (rawUser) {
          sessionStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
          logger.error('Error restoring session from /me', {}, 'UserContext');
        }
        apiClient.clearAuth();
        setCurrentUser(null);
        // Guest /me 401 skips the unauthorized callback (no session yet).
        // Leave auth pages alone; otherwise send deep links to login, not NotFound.
        if (!isGuestAuthPath(window.location.pathname)) {
          navigate(ROUTES.LOGIN, { replace: true });
        }
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    void bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot session restore
  }, []);

  useEffect(() => {
    if (!isInitialized || !currentUser) {
      if (isInitialized && !currentUser) {
        if (selectedRegionCode !== null) setSelectedRegionCode(null);
        if (selectedOrganization !== null || selectedParentOrganization !== null) {
          persistOrganizationSelection(null, null);
        }
      }
      return;
    }

    if (isPlatformUserProfile(currentUser)) {
      if (!selectedRegionCode) {
        const region = getDefaultRegion(currentUser);
        if (region) {
          setSelectedRegionCode(region);
          sessionStorage.setItem(STORAGE_KEYS.SELECTED_REGION, region);
        }
      }
      return;
    }

    if (isTenantUserProfile(currentUser) && !selectedOrganization) {
      const organization = getDefaultOrganization(currentUser.organizations);
      if (organization) {
        persistOrganizationSelection(organization, null);
      }
      return;
    }

    if (isCustomerUserProfile(currentUser) && (!selectedOrganization || !selectedParentOrganization)) {
      const selection = getDefaultCustomerSelection(currentUser);
      if (selection) {
        persistOrganizationSelection(selection.organization, selection.parentOrganization);
      }
    }
  }, [
    currentUser,
    selectedRegionCode,
    selectedOrganization,
    selectedParentOrganization,
    isInitialized,
    persistOrganizationSelection,
  ]);

  const handleRegionChange = useCallback((regionCode: string | null) => {
    setSelectedRegionCode(regionCode);
    if (regionCode != null) {
      sessionStorage.setItem(STORAGE_KEYS.SELECTED_REGION, regionCode);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.SELECTED_REGION);
    }
  }, []);

  const setSelectedOrganizationOnly = useCallback((
    organization: Organization | null,
    parentOrganization: Organization | null = null,
  ) => {
    persistOrganizationSelection(organization, parentOrganization ?? null);
  }, [persistOrganizationSelection]);

  const orgRole = useMemo((): OrgRole | null => {
    if (!currentUser) return null;
    return deriveOrgRoleFromUser(currentUser);
  }, [currentUser]);

  /**
   * Loads the authenticated user from /me after OTP verify (cookie set by backend).
   */
  const completeLogin = useCallback(async () => {
    try {
      const user = await fetchCurrentUser();
      if (!user?.userId) {
        toast.error(ERROR.LOGIN.LOGIN_FAILED);
        navigate(ROUTES.LOGIN, { replace: true });
        return;
      }

      if ((user.availableRegions?.length ?? 0) === 0 && (user.organizations?.length ?? 0) === 0) {
        toast.error(ERROR.LOGIN.NO_ORGANIZATIONS);
        navigate(ROUTES.LOGIN, { replace: true });
        return;
      }

      apiClient.setAuthClaims(user);
      sessionStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
      setCurrentUser(user);
      navigateAfterLogin(user);
    } catch (error) {
      logger.error('Error completing login', error, 'UserContext');
      toast.error(ERROR.LOGIN.LOGIN_FAILED);
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, [navigate, navigateAfterLogin]);

  const clearLocalSession = useCallback((message?: string) => {
    // Navigate before clearing auth so AppShellGate does not swap route trees
    // while still on a platform-only path (which would flash NotFound).
    navigate(ROUTES.LOGIN, { replace: true });
    sessionStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
    sessionStorage.removeItem(STORAGE_KEYS.SELECTED_REGION);
    sessionStorage.removeItem(STORAGE_KEYS.SELECTED_ORGANIZATION);
    sessionStorage.removeItem(STORAGE_KEYS.SELECTED_CUSTOMER_PARENT_ORGANIZATION);
    setCurrentUser(null);
    setSelectedOrganization(null);
    setSelectedParentOrganization(null);
    setSelectedRegionCode(null);
    apiClient.clearAuth();
    queryClient.clear();
    toast.info(message ?? 'You have been logged out, please login again to continue');
  }, [navigate, queryClient]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      logger.error('Error logging out', error, 'UserContext');
    } finally {
      clearLocalSession();
    }
  }, [clearLocalSession]);

  useEffect(() => {
    // On 401: redirect immediately. Do not await /logout — a dead session can hang that call.
    apiClient.setOnUnauthorizedCallback(() => {
      clearLocalSession('Your session has expired. Please log in again.');
      void logout().catch((error) => {
        logger.error('Error clearing server session after 401', error, 'UserContext');
      });
    });

    return () => {
      apiClient.setOnUnauthorizedCallback(() => { });
    };
  }, [clearLocalSession]);

  const value: UserContextType = useMemo(() => ({
    currentUser,
    selectedOrganization,
    selectedParentOrganization,
    selectedRegionCode,
    orgRole,
    isLoading,
    isInitialized,
    completeLogin,
    handleLogout,
    handleRegionChange,
    setSelectedOrganizationOnly,
    refreshAuth,
  }), [
    currentUser,
    selectedOrganization,
    selectedParentOrganization,
    selectedRegionCode,
    orgRole,
    isLoading,
    isInitialized,
    completeLogin,
    handleLogout,
    handleRegionChange,
    setSelectedOrganizationOnly,
    refreshAuth,
  ]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider. Make sure UserProvider wraps your component tree.');
  }

  return context;
};

/** Like useUser but returns undefined when outside UserProvider (e.g. in ErrorBoundary above provider). */
export const useUserOptional = (): UserContextType | undefined => useContext(UserContext);
